import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Download, Search, X, Phone, Mail, MessageCircle,
  Users, Sparkles, CalendarCheck, FileText, Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { leadStatuses, industries, turnoverBands, site } from "@/config/site";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const STATUS_COLORS = {
  NEW: "bg-blue-100 text-blue-800",
  CONTACTED: "bg-indigo-100 text-indigo-800",
  QUALIFIED: "bg-teal-100 text-teal-800",
  "CONSULTATION SCHEDULED": "bg-amber-100 text-amber-800",
  "CONSULTATION COMPLETED": "bg-purple-100 text-purple-800",
  "PROPOSAL SENT": "bg-cyan-100 text-cyan-800",
  WON: "bg-emerald-100 text-emerald-800",
  LOST: "bg-rose-100 text-rose-800",
  "NOT SUITABLE": "bg-slate-200 text-slate-700",
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}><Icon className="h-4.5 w-4.5" /></span>
      </div>
      <p className="mt-3 font-display text-3xl font-bold text-[#18201B]">{value}</p>
      <p className="text-xs font-medium uppercase tracking-wide text-[#526057]">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [filters, setFilters] = useState({ status: "", industry: "", turnover: "", search: "" });

  const fetchStats = useCallback(() => {
    api.get("/leads/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const fetchLeads = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/leads", { params })
      .then((r) => setLeads(r.data))
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const openLead = (lead) => {
    setSelected(lead);
    setEditDraft({
      status: lead.status,
      internal_notes: lead.internal_notes || "",
      consultation_date: lead.consultation_date || "",
      outcome: lead.outcome || "",
      estimated_value: lead.estimated_value ?? "",
      actual_value: lead.actual_value ?? "",
    });
  };

  const saveLead = async () => {
    const payload = {};
    if (editDraft.status) payload.status = editDraft.status;
    if (editDraft.internal_notes !== undefined) payload.internal_notes = editDraft.internal_notes;
    if (editDraft.consultation_date) payload.consultation_date = editDraft.consultation_date;
    if (editDraft.outcome) payload.outcome = editDraft.outcome;
    if (editDraft.estimated_value !== "") payload.estimated_value = parseFloat(editDraft.estimated_value);
    if (editDraft.actual_value !== "") payload.actual_value = parseFloat(editDraft.actual_value);
    try {
      const { data } = await api.patch(`/leads/${selected.id}`, payload);
      toast.success("Lead updated");
      setSelected(data);
      fetchLeads(); fetchStats();
    } catch {
      toast.error("Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "bonsai-leads.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }) : "-");

  return (
    <>
      <Seo title="Lead Dashboard" path="/admin" />
      <div className="min-h-screen bg-[#F9F8F5]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2">
              <img src="/bonsai-logo.png" alt="Bonsai Financial" className="h-9 w-auto rounded-lg bg-white px-2 py-1" />
              <p className="text-[11px] font-medium text-[#526057]">Lead Dashboard</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-btn"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button>
              <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-btn" className="text-[#526057]"><LogOut className="mr-1.5 h-4 w-4" /> Logout</Button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <p className="text-sm text-[#526057]">Welcome back, <span className="font-semibold text-[#18201B]">{user?.name || user?.email}</span></p>

          {/* Stats */}
          {stats && (
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7" data-testid="stats-grid">
              <StatCard icon={Users} label="Total" value={stats.total} accent="bg-slate-100 text-slate-700" />
              <StatCard icon={Sparkles} label="New" value={stats.new} accent="bg-blue-100 text-blue-700" />
              <StatCard icon={Sparkles} label="Qualified" value={stats.qualified} accent="bg-teal-100 text-teal-700" />
              <StatCard icon={CalendarCheck} label="Consultations" value={stats.consultation_scheduled} accent="bg-amber-100 text-amber-700" />
              <StatCard icon={FileText} label="Proposals" value={stats.proposal_sent} accent="bg-cyan-100 text-cyan-700" />
              <StatCard icon={Trophy} label="Won" value={stats.won} accent="bg-emerald-100 text-emerald-700" />
              <StatCard icon={X} label="Lost" value={stats.lost} accent="bg-rose-100 text-rose-700" />
            </div>
          )}

          {/* Filters */}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input placeholder="Search name, email, business, ref…" data-testid="filter-search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9" />
              </div>
              <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="">All statuses</option>
                {leadStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="filter-industry" value={filters.industry} onChange={(e) => setFilters({ ...filters, industry: e.target.value })} className="h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="">All industries</option>
                {industries.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
              </select>
              <select data-testid="filter-turnover" value={filters.turnover} onChange={(e) => setFilters({ ...filters, turnover: e.target.value })} className="h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="">All turnover</option>
                {turnoverBands.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {(filters.status || filters.industry || filters.turnover || filters.search) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ status: "", industry: "", turnover: "", search: "" })} data-testid="filter-clear"><X className="mr-1 h-4 w-4" /> Clear</Button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="leads-table">
                <thead className="border-b border-slate-200 bg-[#F9F8F5] text-xs uppercase tracking-wide text-[#526057]">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3 hidden md:table-cell">Business</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Industry</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Turnover</th>
                    <th className="px-4 py-3 hidden xl:table-cell">Urgency</th>
                    <th className="px-4 py-3 hidden xl:table-cell">Source</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-[#526057]">Loading…</td></tr>
                  ) : leads.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-[#526057]" data-testid="leads-empty">No leads found.</td></tr>
                  ) : (
                    leads.map((l) => (
                      <tr key={l.id} onClick={() => openLead(l)} data-testid={`lead-row-${l.lead_ref}`} className="cursor-pointer hover:bg-[#F9F8F5]">
                        <td className="whitespace-nowrap px-4 py-3 text-[#526057]">{fmtDate(l.created_at)}</td>
                        <td className="px-4 py-3 font-medium text-[#18201B]">{l.first_name} {l.surname}<span className="block font-mono text-[11px] text-slate-400">{l.lead_ref}</span></td>
                        <td className="px-4 py-3 hidden md:table-cell text-[#526057]">{l.business_name || "-"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-[#526057]">{l.industry || "-"}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-[#526057]">{l.turnover_band || "-"}</td>
                        <td className="px-4 py-3 hidden xl:table-cell text-[#526057]">{l.urgency || "-"}</td>
                        <td className="px-4 py-3 hidden xl:table-cell text-[#526057]">{l.source || "-"}</td>
                        <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[l.status] || "bg-slate-100"} border-0 font-medium`}>{l.status}</Badge></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>

        {/* Lead detail drawer */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent className="w-full overflow-y-auto bg-white p-0 sm:max-w-xl" data-testid="lead-detail-drawer">
            {selected && (
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-xs text-[#C68B59]">{selected.lead_ref}</p>
                    <h2 className="font-display text-2xl font-bold text-[#18201B]">{selected.first_name} {selected.surname}</h2>
                    <p className="text-sm text-[#526057]">{fmtDate(selected.created_at)}</p>
                  </div>
                  <Badge className={`${STATUS_COLORS[selected.status]} border-0`}>{selected.status}</Badge>
                </div>

                {/* Quick actions */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-[#133827]" data-testid="lead-call"><Phone className="h-4 w-4" /> Call</a>
                  <a href={`https://wa.me/${selected.phone.replace(/[^0-9]/g, "").replace(/^0/, "27")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-[#1a8a44]" data-testid="lead-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
                  <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-[#133827]" data-testid="lead-email"><Mail className="h-4 w-4" /> Email</a>
                </div>

                {/* Captured info */}
                <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl bg-[#F9F8F5] p-4 text-sm">
                  {[
                    ["Phone", selected.phone], ["Email", selected.email],
                    ["Client type", selected.client_type], ["Business", selected.business_name],
                    ["Industry", selected.industry], ["Province", selected.province],
                    ["Years operating", selected.years_operating], ["Turnover", selected.turnover_band],
                    ["Has accountant", selected.has_accountant], ["Records status", selected.records_status],
                    ["Urgency", selected.urgency], ["Preferred contact", selected.preferred_contact],
                    ["Preferred time", selected.preferred_time],
                  ].map(([k, v]) => (
                    <div key={k}><p className="text-[11px] uppercase tracking-wide text-slate-400">{k}</p><p className="text-[#18201B]">{v || "-"}</p></div>
                  ))}
                  <div className="col-span-2"><p className="text-[11px] uppercase tracking-wide text-slate-400">Services required</p><p className="text-[#18201B]">{(selected.services_required || []).join(", ") || "-"}</p></div>
                  {selected.additional_notes && <div className="col-span-2"><p className="text-[11px] uppercase tracking-wide text-slate-400">Additional notes</p><p className="text-[#18201B]">{selected.additional_notes}</p></div>}
                </div>

                {/* Attribution */}
                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-dashed border-slate-300 p-4 text-sm">
                  <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-[#2C5E43]">Attribution</p>
                  {[
                    ["Source", selected.source], ["UTM Source", selected.utm_source],
                    ["UTM Medium", selected.utm_medium], ["UTM Campaign", selected.utm_campaign],
                    ["UTM Content", selected.utm_content], ["UTM Term", selected.utm_term],
                    ["Landing page", selected.landing_page], ["Referrer", selected.referrer],
                  ].map(([k, v]) => (
                    <div key={k}><p className="text-[11px] uppercase tracking-wide text-slate-400">{k}</p><p className="break-all text-[#18201B]">{v || "-"}</p></div>
                  ))}
                </div>

                {/* Manage */}
                <div className="mt-6 space-y-4">
                  <h3 className="font-display text-lg font-semibold text-[#18201B]">Manage lead</h3>
                  <div>
                    <Label>Status</Label>
                    <select data-testid="lead-status-select" value={editDraft.status} onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                      {leadStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Consultation date</Label><Input type="date" data-testid="lead-consultation-date" value={editDraft.consultation_date} onChange={(e) => setEditDraft({ ...editDraft, consultation_date: e.target.value })} className="mt-1.5" /></div>
                    <div><Label>Outcome</Label><Input data-testid="lead-outcome" value={editDraft.outcome} onChange={(e) => setEditDraft({ ...editDraft, outcome: e.target.value })} className="mt-1.5" placeholder="e.g. Proposal accepted" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Estimated value (R)</Label><Input type="number" data-testid="lead-estimated-value" value={editDraft.estimated_value} onChange={(e) => setEditDraft({ ...editDraft, estimated_value: e.target.value })} className="mt-1.5" /></div>
                    <div><Label>Actual value (R)</Label><Input type="number" data-testid="lead-actual-value" value={editDraft.actual_value} onChange={(e) => setEditDraft({ ...editDraft, actual_value: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div>
                    <Label>Internal notes</Label>
                    <Textarea rows={4} data-testid="lead-notes" value={editDraft.internal_notes} onChange={(e) => setEditDraft({ ...editDraft, internal_notes: e.target.value })} className="mt-1.5" placeholder="Private notes for the team…" />
                  </div>
                  <Button onClick={saveLead} data-testid="lead-save-btn" className="w-full bg-[#133827] text-white hover:bg-[#0d261a]">Save Changes</Button>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
